<template>
  <v-layout column class="fullpage">
    <SavingDialog :opened.sync="savingDialogOpened" />

    <WarningDialog
      :opened="unreviewedFormsWarningDialogOpened"
      :allow-skip="true"
      warning-title="Some pages are not reviewed"
      warning-text='There are some pages that you did not review. Please press "Close" to review the highlighted pages in red or press "Skip" to proceed without reviewing.'
      @close="unreviewedFormsWarningDialogOpened = false"
      @skip="finishReviewing(true)"
    />

    <WarningDialog
      :opened="erroredFormsWarningDialogOpened"
      :allow-skip="false"
      warning-title="Some pages need to be corrected"
      warning-text='There are some fields that are invalid or empty. Please press "Close" to review the highlighted pages below in red.'
      @close="erroredFormsWarningDialogOpened = false"
    />

    <WarningDialog
      :opened="noEmailWarningDialogOpened"
      :allow-skip="false"
      warning-title="Assignee with no email detected"
      warning-text="Please add email for assignee or select 'Use another email' option to proceed."
      @close="noEmailWarningDialogOpened = false"
    />

    <WarningDialog
      :opened="missingRoleWarningDialogOpened"
      :allow-skip="false"
      warning-title="Signer with no assigned role detected"
      warning-text="Please assign a role (Taxpayer or Spouse) for all signers to proceed."
      @close="missingRoleWarningDialogOpened = false"
    />

    <v-dialog v-model="deletedFormsDialogOpened" max-width="600px">
      <v-card>
        <v-card-title>
          <h3>
            Deleted E-Sign Forms
          </h3>
        </v-card-title>

        <v-card-text>
          <v-data-table
            v-if="deletedESignForms.length > 0"
            :headers="deletedFormsTableHeaders"
            :items="deletedESignForms"
            hide-default-footer
          >
            <template v-slot:items="props">
              <td class="text-xs-left">
                {{ getDeletedFormName(props.item) }}
              </td>
              <td>
                <v-tooltip bottom>
                  <v-btn
                    icon
                    small
                    color="primary"
                    @click="restoreDeletedForm(props.index)"
                    slot="activator"
                  >
                    <v-icon>restore</v-icon>
                  </v-btn>
                  <span>Restore this e-sign form</span>
                </v-tooltip>
              </td>
            </template>
          </v-data-table>

          <div v-else class="text-center pa-4">
            No deleted forms to show
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" flat @click="deletedFormsDialogOpened = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <SignaturePadDialog
      :opened.sync="signaturePadOpened"
      :signature-type.sync="signatureType"
      :image-signature.sync="imageSignature"
      :image-raw-data.sync="imageRawData"
      :text-signature.sync="textSignature"
      :saving="savingSignature"
      @saveSignature="saveSignature"
    />

    <!-- Partial: Alerts -->
    <sc-alert v-if="taxReturn.status === 'action_required'" type="info">
      <b>Instruction:</b> Please review every e-signature box placements and
      adjust as necessary.
      <sc-button-group slot="button-group">
        <sc-button
          type="ghost"
          @click="finishReviewing()"
          icon-right="arrow-right"
          :is-disabled="saving || pdfLoading"
          data-test-id="delivery-next-e-sign-forms"
        >
          Finished Reviewing
        </sc-button>
      </sc-button-group>
    </sc-alert>

    <ReadonlyAlert
      v-else
      :tax-return-status="taxReturn.status"
      :disabled="pdfLoading"
      @next="next"
    />

    <v-layout column class="outer-container">
      <v-layout row class="inner-container">
        <!-- Filing Instructions Panel (LEFT) -->
        <FilingInstructionsPanel
          v-if="filingInstructionsPosition === 'left'"
          ref="filingInstructionsPanel"
          :taxReturn="taxReturn"
          :position="filingInstructionsPosition"
          :visible="filingInstructionsVisible"
          :highlighted-block-numbers="highlightedBlockNumbers"
          @update:highlighted-block-numbers="
            $emit('update:highlightedBlockNumbers', $event)
          "
          @update:position="$emit('update:filingInstructionsPosition', $event)"
          @update:visible="$emit('update:filingInstructionsVisible', $event)"
        />

        <!-- Partial: Sidebar -->
        <sc-flex class="sidebar" :is-column="true" :gap="8">
          <sc-h5>E-Sign Forms</sc-h5>
          <ul class="page-list">
            <li
              v-for="(form, index) in taxReturn.e_sign_forms"
              :key="form.page"
              :class="{
                selected: currentFormIndex === index + 1,
                unreviewed: shouldHighlightUnreviewedForms && !form.reviewed_by,
                errored: shouldHighlightErroredForms && isFormInvalid(form),
              }"
            >
              <a @click="currentFormIndex = index + 1">
                <v-layout>
                  <v-icon class="mr-2" small>
                    {{
                      shouldHighlightErroredForms && isFormInvalid(form)
                        ? 'error'
                        : 'bookmark_border'
                    }}
                  </v-icon>

                  <span v-if="form.form_id === 'other'">
                    <span v-if="!isEmpty(form.custom_form_name)">
                      {{ form.custom_form_name }}
                    </span>
                    <span v-else>
                      Other
                    </span>
                  </span>
                  <span v-else-if="getFormDefinitionById(form.form_id)">
                    {{ getFormDefinitionById(form.form_id).name }} ({{
                      form.form_id
                    }})
                  </span>
                  <span v-else>
                    Unknown
                    <span
                      v-if="form.status === 'analyzing'"
                      class="grey--text body-1"
                    >
                      (analyzing...)
                    </span>
                  </span>
                </v-layout>
              </a>
            </li>
          </ul>

          <v-btn
            v-if="deletedESignForms.length > 0"
            class="text-none ma-0 mt-2"
            type="default"
            @click="deletedFormsDialogOpened = true"
            outline
            color="darkgrey"
          >
            <v-icon small class="mr-2">history</v-icon>
            View deleted forms ({{ deletedESignForms.length }})
          </v-btn>
        </sc-flex>

        <!-- Partial: Content -->
        <div class="pdf-container" v-if="currentForm">
          <!-- Partial: Toolbar -->
          <nav class="toolbar">
            <sc-flex
              class="toolbar__pageNavigation"
              :is-centered="true"
              :gap="8"
            >
              <sc-button-group>
                <sc-button
                  type="secondary"
                  @click="currentFormIndex -= 1"
                  :is-disabled="currentFormIndex === 1"
                  :is-icon-only="true"
                  icon-left="arrow-left"
                  size="small"
                ></sc-button>
              </sc-button-group>
              <sc-flex :is-y-centered="true" :gap="8">
                <sc-small>Page:</sc-small>
                <input
                  :min="1"
                  :max="formCount"
                  type="number"
                  :value="currentFormIndex"
                  @change="updateCurrentFormIndex($event)"
                />
                <sc-small>/ {{ formCount }}</sc-small>
              </sc-flex>
              <sc-button-group>
                <sc-button
                  type="secondary"
                  @click="currentFormIndex += 1"
                  :is-disabled="currentFormIndex === formCount"
                  :is-icon-only="true"
                  icon-right="arrow-right"
                  size="small"
                >
                </sc-button>
              </sc-button-group>
            </sc-flex>

            <sc-flex
              class="toolbar__pageActions"
              x-align="right"
              :is-y-centered="true"
              :gap="8"
            >
              <v-switch
                :input-value="filingInstructionsVisible"
                @change="toggleFilingInstructions"
                class="my-0 py-0"
                hide-details
              >
                <template v-slot:label>
                  <span class="body-2">Show Filing Instructions</span>
                </template>
              </v-switch>

              <v-menu
                transition="slide-y-transition"
                bottom
                left
                nudge-bottom="30"
                class="my-1 toolbar__downloadMenu"
              >
                <sc-button-group slot="activator">
                  <sc-button
                    type="secondary"
                    size="small"
                    icon-left="caret-down"
                    data-test-id="download-button"
                  >
                    Download
                  </sc-button>
                </sc-button-group>

                <v-list>
                  <v-list-tile @click="download('current-form-only')">
                    <v-list-tile-title>
                      Download the current page only
                    </v-list-tile-title>
                  </v-list-tile>

                  <v-list-tile @click="download('signed-only')">
                    <v-list-tile-title>
                      Download forms containing signatures
                    </v-list-tile-title>
                  </v-list-tile>

                  <v-list-tile @click="download('all')">
                    <v-list-tile-title>
                      Download all forms (signed and unsigned)
                    </v-list-tile-title>
                  </v-list-tile>
                </v-list>
              </v-menu>
            </sc-flex>
          </nav>

          <!-- Partial: Content -->
          <v-layout class="page-container">
            <!-- Partial: Scroll -->
            <v-layout class="scrollable-container">
              <v-layout class="pdf-page-container">
                <v-layout v-if="hasWetSignatures" column class="my-3 mx-3">
                  <v-layout class="my-2">
                    <h3>
                      The client uploaded the following documents containing wet
                      signature(s):
                    </h3>
                  </v-layout>

                  <SignedDocumentList
                    :tax-return="taxReturn"
                    :current-company="currentCompany"
                    :current-form="currentForm"
                  />
                </v-layout>

                <v-divider v-if="hasWetSignatures" class="my-2"></v-divider>
                <span class="body-2 grey--text mb-2" v-if="hasWetSignatures"
                  >ORIGINAL FORM</span
                >

                <v-layout
                  v-if="pdfLoading"
                  class="mt-4"
                  justify-center
                  align-center
                >
                  <v-progress-circular indeterminate color="primary" />
                </v-layout>

                <div id="pdf-annotation" class="pdf ph-no-capture">
                  <img
                    v-if="
                      pdfLoaded &&
                        pageImages &&
                        pageImages[currentForm.page - 1]
                    "
                    class="page-image"
                    :src="pageImages[currentForm.page - 1]"
                    :style="{
                      width: pdfPageWidth + 'px',
                      height: pdfPageHeight + 'px',
                    }"
                  />
                </div>

                <v-layout
                  v-if="pdfLoaded && !currentForm.wetSignaturesRequired"
                  class="main-signature-container"
                  v-for="(signature, index) in currentForm.signatures"
                  :key="index"
                >
                  <!-- Signature Container -->
                  <v-layout
                    class="signature-container"
                    :data-signatureid="index"
                    :style="
                      getStylePosition({
                        pageWidth: signature.pageWidth,
                        elementBottom: signature.signatureBottom,
                        elementLeft: signature.signatureLeft,
                      })
                    "
                  >
                    <!-- Signature: Image -->
                    <img
                      v-if="signature.signatureType === 'image'"
                      :src="signature.signatureData"
                      :style="
                        getStyleDimension({
                          pageWidth: signature.pageWidth,
                          elementWidth: signature.signatureWidth,
                          elementHeight: signature.signatureHeight,
                        })
                      "
                    />

                    <!-- Signature: Text -->
                    <v-layout
                      v-else-if="signature.signatureType === 'text'"
                      align-center
                      justify-center
                      :style="
                        getStyleDimension({
                          pageWidth: signature.pageWidth,
                          elementWidth: signature.signatureWidth,
                          elementHeight: signature.signatureHeight,
                        })
                      "
                    >
                      <span
                        class="signature-text"
                        :style="
                          getStyleSignatureText({
                            pageWidth: signature.pageWidth,
                            fontSize: signature.signatureTextSize,
                          })
                        "
                      >
                        {{ signature.signatureData }}
                      </span>
                    </v-layout>

                    <!-- Signature: Area -->
                    <v-layout
                      v-else
                      class="sign-area"
                      :style="
                        getStyleDimension({
                          pageWidth: signature.pageWidth,
                          elementWidth: signature.signatureWidth,
                          elementHeight: signature.signatureHeight,
                        })
                      "
                    >
                      <div
                        class="signer-title"
                        :style="
                          getStyleForSignatureTitle({
                            pageWidth: signature.pageWidth,
                            fontSize: signature.signatureTextSize,
                          })
                        "
                      >
                        {{ signature.title }}
                        <span v-if="signature.name"
                          >({{ signature.name }})</span
                        >
                      </div>
                      <h2
                        :style="
                          getStyleForSignatureHeader({
                            pageWidth: signature.pageWidth,
                            fontSize: signature.signatureTextSize,
                          })
                        "
                      >
                        Signature
                        <v-tooltip bottom>
                          <v-btn
                            slot="activator"
                            v-if="signature.title === 'Yourself'"
                            icon
                            small
                            @click="openSignDialog(index)"
                          >
                            <v-icon>edit</v-icon>
                          </v-btn>
                          <span>
                            Sign as yourself
                          </span>
                        </v-tooltip>
                      </h2>
                    </v-layout>
                  </v-layout>

                  <!-- Signature Date Container -->
                  <v-layout
                    class="signature-date-container"
                    :class="{
                      'd-none': signature.hideSignatureDate,
                    }"
                    :data-signatureid="index"
                    :style="
                      getStylePosition({
                        pageWidth: signature.pageWidth,
                        elementBottom: signature.signatureDateBottom,
                        elementLeft: signature.signatureDateLeft,
                      })
                    "
                  >
                    <v-layout
                      v-if="signature.signatureDate"
                      align-center
                      justify-center
                      :style="
                        getStyleDimension({
                          pageWidth: signature.pageWidth,
                          elementWidth: signature.signatureDateWidth,
                          elementHeight: signature.signatureDateHeight,
                        })
                      "
                    >
                      <span
                        class="signature-date"
                        :style="
                          getStyleSignatureDateText({
                            pageWidth: signature.pageWidth,
                            fontSize: signature.signatureTextSize,
                          })
                        "
                      >
                        {{ signature.signatureDate }}
                      </span>
                    </v-layout>

                    <v-layout
                      v-else
                      class="sign-area"
                      :style="
                        getStyleDimension({
                          pageWidth: signature.pageWidth,
                          elementWidth: signature.signatureDateWidth,
                          elementHeight: signature.signatureDateHeight,
                        })
                      "
                    >
                      <div
                        class="signer-title"
                        :style="
                          getStyleForSignatureTitle({
                            pageWidth: signature.pageWidth,
                            fontSize: signature.signatureTextSize,
                          })
                        "
                      >
                        {{ signature.title }}
                        <span v-if="signature.name"
                          >({{ signature.name }})</span
                        >
                      </div>
                      <h2
                        :style="
                          getStyleForSignatureHeader({
                            pageWidth: signature.pageWidth,
                            fontSize: signature.signatureTextSize,
                          })
                        "
                      >
                        Date
                      </h2>
                    </v-layout>
                  </v-layout>
                </v-layout>
              </v-layout>
            </v-layout>

            <!-- Partial: Sidebar -->
            <div class="sidebar--right">
              <v-form ref="form">
                <v-layout
                  column
                  class="debug-container"
                  v-if="
                    shouldShowDebugingTools &&
                      taxReturnSignatureDebuggingEnabled
                  "
                >
                  <h3 class="mb-2">Internal Use Only:</h3>

                  <span>
                    <b>Important</b>: Please ensure all signature and date
                    positions on this page are accurate before clicking the
                    Collect button.
                  </span>

                  <v-btn
                    color="primary"
                    class="ma-0 text-none my-2"
                    :disabled="pdfLoading || collectingSignatureData"
                    :loading="collectingSignatureData"
                    @click="collectSignatureData"
                  >
                    <v-icon small left>publish</v-icon>
                    Collect signature positions
                  </v-btn>

                  <v-btn
                    color="secondary"
                    class="ma-0 text-none my-2"
                    :disabled="pdfLoading || autoPlacingSignatures"
                    :loading="autoPlacingSignatures"
                    @click="getSignaturePlacements"
                  >
                    <v-icon small left>auto_awesome</v-icon>
                    Auto-place signatures
                  </v-btn>
                </v-layout>

                <h3>Please review signing details below for this form.</h3>

                <div class="mt-3 form-control" v-if="currentForm">
                  <span>Form type: <span class="error--text">*</span></span>

                  <v-autocomplete
                    solo
                    hide-details
                    class="mt-1"
                    :items="formDefinitions"
                    :item-text="
                      item => item.name + (item.id ? ' - ' + item.id : '')
                    "
                    item-value="id"
                    :placeholder="
                      currentForm.status === 'analyzing'
                        ? 'Analyzing... Please wait'
                        : 'Select form type'
                    "
                    :value="currentForm.form_id"
                    @change="currentForm.form_id = $event"
                    :disabled="!editable || currentForm.status === 'analyzing'"
                    :rules="[rules.required]"
                  />
                </div>

                <div
                  class="mt-3 form-control"
                  v-if="currentForm && currentForm.form_id === 'other'"
                >
                  <span>Form name:</span>

                  <v-combobox
                    solo
                    hide-details
                    clearable
                    class="mt-1 form-control"
                    :items="customFormNames"
                    item-text="name"
                    item-value="name"
                    :value="currentForm.custom_form_name"
                    :append-icon="null"
                    :disabled="!editable"
                    :rules="[rules.required]"
                    @change="handleCustomFormChange($event)"
                  >
                    <v-layout
                      slot="item"
                      slot-scope="props"
                      align-center
                      justify-space-between
                    >
                      <span>
                        {{ props.item.name }}
                      </span>

                      <v-btn
                        icon
                        ripple
                        @click.prevent.stop="deleteCustomFormName(props.item)"
                      >
                        <v-icon
                          color="error lighten-1"
                          class="material-icons-outlined"
                          >delete</v-icon
                        >
                      </v-btn>
                    </v-layout>
                  </v-combobox>
                </div>

                <v-divider class="my-4"></v-divider>

                <div v-if="currentForm">
                  <h3 class="font-weight-bold">Signers</h3>

                  <div
                    v-for="(signature, index) in currentForm.signatures"
                    :key="index"
                    class="signer-container pa-4 mb-4"
                  >
                    <v-layout
                      justify-space-between
                      class="mb-3 font-weight-bold"
                    >
                      <span>
                        Signer {{ index + 1 }}
                        <span class="error--text">*</span>
                      </span>
                      <v-icon
                        small
                        color="error"
                        class="material-icons-outlined"
                        @click="removeSigner(index)"
                        v-if="editable"
                      >
                        remove_circle
                      </v-icon>
                    </v-layout>

                    <template>
                      <v-layout align-center class="form-control mb-3">
                        <v-select
                          solo
                          hide-details
                          :value="signature.selectedSigner"
                          placeholder="Select signer"
                          :items="newSelectableNames"
                          :item-text="item => item.displayText"
                          item-value="id"
                          return-object
                          :disabled="!editable"
                          :rules="[rules.required]"
                          @change="handleSignerChange($event, signature, index)"
                        >
                          <template v-slot:item="{ item }">
                            <v-list-item-content>
                              <v-list-item-title>{{
                                item.displayText
                              }}</v-list-item-title>
                              <v-list-item-subtitle>
                                <v-chip
                                  small
                                  color="primary"
                                  text-color="white"
                                  v-if="item.role"
                                >
                                  {{ item.role }}
                                </v-chip>
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </template>
                          <template v-slot:selection="{ item }">
                            <span>{{ item.full_name }}</span>
                          </template>
                        </v-select>
                      </v-layout>

                      <v-divider
                        v-if="signature.selectedSigner"
                        class="my-4"
                      ></v-divider>

                      <v-layout
                        align-center
                        class="form-control mb-2"
                        v-if="signature.selectedSigner"
                      >
                        <label class="label mr-3">Title:</label>
                        <span v-if="signature.title">{{
                          signature.title
                        }}</span>
                        <span v-else class="error--text">No assigned role</span>
                      </v-layout>

                      <v-layout
                        align-center
                        class="form-control mb-2"
                        v-if="signature.selectedSigner"
                      >
                        <label class="label mr-3">Name:</label>
                        <span>{{ signature.selectedSigner.full_name }}</span>
                      </v-layout>

                      <v-layout
                        align-center
                        class="form-control mb-2"
                        v-if="signature.selectedSigner"
                      >
                        <label class="label mr-3">Email:</label>
                        <span
                          :class="{
                            'error--text': isTestEmail(
                              signature.selectedSigner.email,
                            ),
                          }"
                          class="email-display"
                          :title="
                            isTestEmail(signature.selectedSigner.email)
                              ? 'No Email'
                              : signature.selectedSigner.email
                          "
                        >
                          {{
                            isTestEmail(signature.selectedSigner.email)
                              ? 'No Email'
                              : signature.selectedSigner.email
                          }}
                        </span>
                      </v-layout>

                      <v-layout
                        column
                        class="form-control mb-2"
                        v-if="signature.selectedSigner && !signature.title"
                      >
                        <v-divider class="mb-4"></v-divider>
                        <label class="label mb-2"
                          >Assign role <span class="error--text">*</span></label
                        >
                        <v-select
                          solo
                          hide-details
                          :items="['Taxpayer', 'Spouse']"
                          placeholder="Select role"
                          v-model="signature.title"
                          @change="handleRoleChange(signature)"
                          :disabled="!editable"
                          dense
                        />
                      </v-layout>

                      <v-layout
                        column
                        class="form-control mb-2"
                        v-if="
                          signature.selectedSigner &&
                            isTestEmail(signature.selectedSigner.email)
                        "
                      >
                        <v-divider class="mb-4"></v-divider>
                        <label class="label mb-2"
                          >Use another email
                          <span class="error--text">*</span></label
                        >
                        <v-select
                          solo
                          hide-details
                          v-model="
                            signature.selectedSigner.alternative_signer_id
                          "
                          placeholder="Select email"
                          :items="getAvailableEmails()"
                          :item-text="item => item.displayText"
                          :item-value="item => item.id"
                          :disabled="!editable"
                          @change="handleAlternativeSignerChange(signature)"
                          dense
                        />
                      </v-layout>

                      <v-layout>
                        <v-switch
                          v-model="signature.hideSignatureDate"
                          label="Hide date field"
                          class="mt-0"
                          hide-details
                          :disabled="!editable"
                        >
                          <template v-slot:label>
                            <span class="body-2">Hide date field</span>
                          </template>
                        </v-switch>
                      </v-layout>
                    </template>

                    <!-- Old signature flow (fallback) -->
                    <!-- <template v-else>
                      <v-layout align-center class="form-control">
                        <label class="label"
                          >Title: <span class="error--text">*</span></label
                        >

                        <v-select
                          solo
                          hide-details
                          :items="selectableTitles"
                          v-model="signature.title"
                          :disabled="!editable"
                          :rules="[rules.required]"
                          @change="handleTitleChange($event, signature, index)"
                        />
                      </v-layout>

                      <v-layout align-center class="form-control">
                        <label class="label"
                          >Name: <span class="error--text">*</span></label
                        >

                        <v-select
                          v-if="signature.title !== 'Yourself'"
                          solo
                          hide-details
                          v-model="signature.name"
                          placeholder="Select signer"
                          :items="selectableNames"
                          :disabled="!editable"
                          :rules="[rules.required]"
                          @change="handleNameChange($event, signature, index)"
                        />

                        <span v-else>{{ currentUser.full_name }}</span>
                      </v-layout>

                      <v-layout
                        align-center
                        class="form-control"
                        v-if="signature.title !== 'Yourself'"
                      >
                        <label class="label"
                          >Email: <span class="error--text">*</span></label
                        >

                        <v-select
                          solo
                          hide-details
                          v-model="signature.userId"
                          placeholder="Select email to send to"
                          :items="selectableOwners"
                          :item-text="item => item.email"
                          :item-value="item => item.id"
                          :disabled="!editable"
                          :rules="[rules.required]"
                        />
                      </v-layout>
                    </template> -->
                  </div>

                  <v-btn
                    color="primary"
                    class="ma-0 mt-4 text-none"
                    :disabled="
                      !editable ||
                        pdfLoading ||
                        currentForm.status === 'analyzing'
                    "
                    @click="addSigner"
                  >
                    <v-icon>add</v-icon>
                    Add signer
                  </v-btn>
                </div>

                <v-divider class="my-4"></v-divider>

                <v-layout>
                  <v-btn
                    color="error"
                    class="text-none"
                    block
                    flat
                    @click="removeForm"
                    :disabled="!editable || currentForm.status === 'analyzing'"
                  >
                    <v-icon left class="material-icons-outlined">delete</v-icon>
                    Remove this form
                  </v-btn>
                </v-layout>
              </v-form>
            </div>
          </v-layout>
        </div>

        <!-- Filing Instructions Panel (RIGHT) -->
        <FilingInstructionsPanel
          v-if="filingInstructionsPosition === 'right'"
          ref="filingInstructionsPanel"
          :taxReturn="taxReturn"
          :position="filingInstructionsPosition"
          :visible="filingInstructionsVisible"
          :highlighted-block-numbers="highlightedBlockNumbers"
          @update:highlighted-block-numbers="
            $emit('update:highlightedBlockNumbers', $event)
          "
          @update:position="$emit('update:filingInstructionsPosition', $event)"
          @update:visible="$emit('update:filingInstructionsVisible', $event)"
        />
      </v-layout>

      <!-- Filing Instructions BOTTOM -->
      <FilingInstructionsPanel
        v-if="filingInstructionsPosition === 'bottom'"
        ref="filingInstructionsPanel"
        :taxReturn="taxReturn"
        :position="filingInstructionsPosition"
        :visible="filingInstructionsVisible"
        :highlighted-block-numbers="highlightedBlockNumbers"
        @update:highlighted-block-numbers="
          $emit('update:highlightedBlockNumbers', $event)
        "
        @update:position="$emit('update:filingInstructionsPosition', $event)"
        @update:visible="$emit('update:filingInstructionsVisible', $event)"
      />
    </v-layout>
  </v-layout>
</template>

<script>
import pdf from 'vue-pdf';
import {
  PDFJS_CMAP_URL,
  PDFJS_STANDARD_FONT_DATA_URL,
  PDFJS_WORKER_SRC,
} from '@/utils/pdf';
import API from '@/api';
import interact from 'interactjs';
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import chunk from 'lodash/chunk';
import uniqBy from 'lodash/uniqBy';
import { mapGetters } from 'vuex';
import { PDFDocument, rgb, degrees } from 'pdf-lib';
import axios from 'axios';
import ReadonlyAlert from '@/components/returns/ReadonlyAlert';
import download from 'downloadjs';
import fontkit from '@pdf-lib/fontkit';
import SignedDocumentList from '@/components/returns/SignedDocumentList';
import WarningDialog from '@/components/returns/WarningDialog';
import SavingDialog from '@/components/returns/SavingDialog';
import JSZip from 'jszip';
import UtilsMixin from '@/mixins/UtilsMixins';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';
import { Sema } from 'async-sema';
import SignaturePadDialog from '@/components/dialogs/SignaturePadDialog';
import moment from 'moment';
import FilingInstructionsPanel from '@/components/returns/FilingInstructionsPanel';
import FilingInstructionsPanelMixin from '@/mixins/FilingInstructionsPanelMixin';

export default {
  components: {
    pdf,
    ReadonlyAlert,
    SignedDocumentList,
    WarningDialog,
    SavingDialog,
    SignaturePadDialog,
    FilingInstructionsPanel,
  },

  mixins: [UtilsMixin, FirmPreferenceMixins, FilingInstructionsPanelMixin],

  props: {
    currentCompany: {
      type: Object,
      default: () => {},
    },

    taxReturn: {
      type: Object,
      default: () => {},
    },

    filingInstructionsPosition: {
      type: String,
    },

    filingInstructionsVisible: {
      type: Boolean,
      default: false,
    },

    highlightedBlockNumbers: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      currentFormIndex: 1,

      MAX_PDF_WIDTH: 918,

      pdfData: null,
      pdfDoc: null,
      pdfLoading: false,
      pdfProgress: 0,
      pdfLoaded: false,
      pdfPageIndex: 1,
      pdfPageWidth: 0,
      pdfPageHeight: 0,
      pageImages: null,

      saving: false,

      // States for forms with errors
      erroredFormsWarningDialogOpened: false,
      shouldHighlightErroredForms: false,

      // States for unreviewed forms
      unreviewedFormsWarningDialogOpened: false,
      shouldHighlightUnreviewedForms: false,

      // State for no email warning
      noEmailWarningDialogOpened: false,

      // State for missing role warning
      missingRoleWarningDialogOpened: false,

      savingDialogOpened: false,

      formDefinitions: [],
      defaultSignatureValues: null,
      selectableTitles: ['Taxpayer', 'Spouse', 'Yourself'],
      customFormNames: [],

      rules: {
        required: value => !!value || 'Required.',
      },

      signaturePadOpened: false,
      signatureType: 'image',
      imageSignature: '',
      imageRawData: null,
      textSignature: '',
      savingSignature: false,
      currentSignatureIndex: null,
      savedSignaturesByName: {},

      shouldShowDebugingTools: false,
      collectingSignatureData: false,
      autoPlacingSignatures: false,

      deletedESignForms: [],
      deletedFormsDialogOpened: false,
      deletedFormsTableHeaders: [
        { text: 'Form Name', value: 'form_name', align: 'left' },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center' },
      ],
    };
  },

  computed: {
    ...mapGetters({
      currentUser: 'user',
      currentUserFirm: 'currentFirm',
    }),

    hasWetSignatures() {
      return (
        this.currentForm &&
        this.currentForm.wetSignatures &&
        this.currentForm.wetSignatures.length > 0
      );
    },

    currentForm() {
      return this.taxReturn.e_sign_forms[this.currentFormIndex - 1];
    },

    formCount() {
      return this.taxReturn.e_sign_forms.length;
    },

    newSelectableNames() {
      const companyUsers = this.currentCompany.all_owners.map(owner => {
        const assignment =
          owner.assignments &&
          owner.assignments.find(
            assignment => assignment.company_id === this.currentCompany.id,
          );

        const role = this.getRole(assignment);

        return {
          id: owner.id,
          full_name: owner.full_name,
          email: owner.email,
          role: role,
          displayText: `${owner.full_name} (${this.getDisplayEmail(
            owner.email,
          )})`,
        };
      });

      // Add "Yourself" option at the beginning
      return [
        {
          id: this.currentUser.id,
          full_name: this.currentUser.full_name,
          email: this.currentUser.email,
          role: 'Yourself',
          displayText: 'Yourself',
        },
        ...companyUsers,
      ];
    },

    selectableNames() {
      return this.currentCompany.all_owners.map(owner => owner.full_name);
    },

    selectableOwners() {
      return this.currentCompany.owners.filter(owner => owner.email);
    },
    editable() {
      return this.taxReturn.status === 'action_required';
    },

    hasNoEmail(signature) {
      return (
        signature.selectedSigner &&
        this.getDisplayEmail(signature.selectedSigner.email) === 'No Email'
      );
    },

    hasIncompleteNoEmailSigners() {
      // Check all forms for signers with "No Email" that need alternative email selected
      return this.taxReturn.e_sign_forms.some(form => {
        return (
          form.signatures &&
          form.signatures.some(signature => {
            // Check if signer has test email and is using another email
            const hasTestEmail =
              signature.selectedSigner &&
              this.isTestEmail(signature.selectedSigner.email);

            // If they have test email, they must select an alternative signer
            if (hasTestEmail) {
              return !signature.selectedSigner.alternative_signer_id; // Incomplete if no alternative signer selected
            }

            return false;
          })
        );
      });
    },

    hasSignersWithMissingRole() {
      // Check all forms for signers with no assigned role
      // signature.title is the source of truth (saved to DB)
      return this.taxReturn.e_sign_forms.some(form => {
        return (
          form.signatures &&
          form.signatures.some(signature => {
            if (!signature.selectedSigner) {
              return false; // No signer selected, skip
            }
            // Check signature.title (source of truth) - selectedSigner.role is read-only from matched signer
            const hasRole = signature.title;
            return !hasRole || hasRole.trim() === '';
          })
        );
      });
    },
  },

  methods: {
    isEmpty,

    moment,

    getRole(assignment) {
      if (!assignment) {
        return '';
      }

      let role = '';
      if (assignment.role === 'spouse') {
        role = 'Spouse';
      } else if (assignment.is_taxpayer === true) {
        role = 'Taxpayer';
      }
      return role;
    },

    async collectSignatureData() {
      try {
        this.collectingSignatureData = true;

        // Make sure we have the latest signature data before collecting them
        this.checkAndFixPageDimension(this.currentForm);
        await this.save(true);

        await API.taxReturn.collectSignatureDataForForm({
          id: this.taxReturn.id,
          companyId: this.currentCompany.id,
          formIndex: this.currentFormIndex - 1,
        });

        this.$store.dispatch('application/setSuccessNotification', {
          message: 'Successfully collected signature data for the form.',
        });
      } catch (error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Failed to collect signature data for the form.',
        });
      } finally {
        this.collectingSignatureData = false;
      }
    },

    async getSignaturePlacements() {
      try {
        this.autoPlacingSignatures = true;

        for (const signature of this.currentForm.signatures) {
          const response = await API.taxReturn.getSignaturePlacement({
            id: this.taxReturn.id,
            companyId: this.currentCompany.id,
            page: this.currentForm.page,
            title: signature.title,
          });

          const {
            signature_prediction: signaturePrediction,
            date_prediction: datePrediction,
            image_width: imageWidth,
            image_height: imageHeight,
          } = response.data;

          const pageWidth = this.pdfPageWidth;
          const pageHeight = this.pdfPageHeight;

          const signatureLeft =
            signaturePrediction.position.left * (pageWidth / imageWidth);
          const signatureBottom =
            signaturePrediction.position.bottom * (pageHeight / imageHeight);

          signature.pageWidth = pageWidth;
          signature.pageHeight = pageHeight;
          signature.signatureLeft = signatureLeft;
          signature.signatureBottom = signatureBottom;
          signature.shouldCollectSignaturePosition = false;

          if (datePrediction) {
            const signatureDateLeft =
              datePrediction.position.left * (pageWidth / imageWidth);
            const signatureDateBottom =
              datePrediction.position.bottom * (pageHeight / imageHeight);

            signature.signatureDateLeft = signatureDateLeft;
            signature.signatureDateBottom = signatureDateBottom;
            signature.shouldCollectDatePosition = false;
          }
        }
      } catch (error) {
        console.error('Error getting signature placements:', error);
      } finally {
        // Add a small delay to allow users to see the animation.
        await this.sleep(500);
        this.autoPlacingSignatures = false;
      }
    },

    openSignDialog(signatureIndex) {
      this.signaturePadOpened = true;
      this.currentSignatureIndex = signatureIndex;

      const signature = this.currentForm.signatures[signatureIndex];
      const savedSignature = this.savedSignaturesByName[signature.name];

      this.signatureType = savedSignature
        ? savedSignature.signatureType
        : 'image';
      this.imageSignature =
        savedSignature && savedSignature.signatureType === 'image'
          ? savedSignature.signatureData
          : '';
      this.textSignature =
        savedSignature && savedSignature.signatureType === 'text'
          ? savedSignature.signatureData
          : '';
    },

    handleTitleChange(title, signature, index) {
      if (title === 'Yourself') {
        signature.name = this.currentUser.full_name;
        signature.userId = this.currentUser.id;
        this.openSignDialog(index);
      } else {
        if (this.currentCompany.owners.length === 1) {
          signature.name = this.currentCompany.owners[0].full_name;
          signature.userId = this.currentCompany.owners[0].id;
        } else {
          signature.name = '';
          signature.userId = null;
        }
      }
    },

    handleNameChange(selectedName, signature) {
      const selectedUser = this.selectableNames.find(
        user => user.full_name === selectedName,
      );
      if (selectedUser) {
        signature.name = selectedUser.full_name;
        signature.userId = selectedUser.id;
      }
    },

    handleAlternativeSignerChange(signature) {
      if (signature.selectedSigner) {
        // Update userId based on alternative signer selection
        if (signature.selectedSigner.alternative_signer_id) {
          this.$set(
            signature,
            'userId',
            signature.selectedSigner.alternative_signer_id,
          );
        } else {
          this.$set(signature, 'userId', signature.selectedSigner.id);
        }
        // Trigger re-evaluation of computed properties
        this.$nextTick(() => {
          // Force a re-render to ensure computed properties update
          this.$forceUpdate();
        });
      }
    },

    handleRoleChange(signature) {
      if (signature.title) {
        // signature.title is the source of truth - it's already updated by v-model
        // selectedSigner.role is read-only and should not be modified
        // Save the changes
        this.save(true);
      }
    },

    handleSignerChange(selectedSigner, signature, index) {
      if (selectedSigner) {
        // Initialize alternative_signer_id if it doesn't exist
        if (!selectedSigner.hasOwnProperty('alternative_signer_id')) {
          this.$set(selectedSigner, 'alternative_signer_id', null);
        }

        // Update the signature with the selected signer's information
        signature.selectedSigner = selectedSigner;

        // @Lisia: TODO depercate these fields once ensured that the new signature flow is working
        signature.name = selectedSigner.full_name;

        // Set userId based on alternative signer usage
        if (selectedSigner.alternative_signer_id) {
          // User is using an alternative signer
          signature.userId = selectedSigner.alternative_signer_id;
        } else {
          // Use the original signer's ID
          signature.userId = selectedSigner.id;
        }

        // signature.title is the source of truth - only set it if it's empty
        // selectedSigner.role is read-only from the matched signer (suggestion only)
        // Don't overwrite existing signature.title if it's already set
        // Only use selectedSigner.role as fallback if it's a valid role (not empty)
        // This ensures we don't auto-assign roles when the assignment doesn't have one
        if (
          !signature.title &&
          selectedSigner.role &&
          selectedSigner.role.trim() !== ''
        ) {
          signature.title = selectedSigner.role;
        }

        // If it's "Yourself", open the sign dialog immediately
        if (selectedSigner.role === 'Yourself') {
          this.openSignDialog(index);
        }
      }
    },

    matchExistingSigner(signature) {
      // PRIORITY 1: Try to match by selectedSigner.id first (from database)
      if (signature.selectedSigner && signature.selectedSigner.id) {
        const matchedSigner = this.newSelectableNames.find(
          signer => signer.id === signature.selectedSigner.id,
        );
        if (matchedSigner) {
          const alternative_signer_id =
            signature.selectedSigner.alternative_signer_id || null;

          // signature.title is the source of truth (saved to DB) - preserve it
          // selectedSigner.role is read-only from matchedSigner (for display/suggestion only)
          // Only use matchedSigner.role as fallback if signature.title is empty
          const preservedTitle = signature.title || '';

          // Restore selectedSigner with read-only role from matched signer
          signature.selectedSigner = {
            id: matchedSigner.id,
            full_name: matchedSigner.full_name,
            email: matchedSigner.email,
            role: matchedSigner.role, // Read-only from matched signer
            displayText: matchedSigner.displayText,
            isYourself: matchedSigner.isYourself,
            alternative_signer_id: alternative_signer_id,
          };

          // If signature.title is empty and matchedSigner has a valid role, use it as fallback
          // Only use matchedSigner.role if it's not empty (assignment might not have a role)
          if (
            !preservedTitle &&
            matchedSigner.role &&
            matchedSigner.role.trim() !== ''
          ) {
            signature.title = matchedSigner.role;
          } else if (preservedTitle) {
            // Ensure signature.title is preserved (source of truth)
            signature.title = preservedTitle;
          }
          return;
        }
      }

      // PRIORITY 2: If no selectedSigner.id, try to match by userId
      if (signature.userId) {
        const matchedSigner = this.newSelectableNames.find(
          signer => signer.id === signature.userId,
        );
        if (matchedSigner) {
          // signature.title is the source of truth (saved to DB) - preserve it
          // selectedSigner.role is read-only from matchedSigner
          const preservedTitle = signature.title || '';

          // Create new selectedSigner with read-only role from matched signer
          signature.selectedSigner = {
            id: matchedSigner.id,
            full_name: matchedSigner.full_name,
            email: matchedSigner.email,
            role: matchedSigner.role, // Read-only from matched signer
            displayText: matchedSigner.displayText,
            isYourself: matchedSigner.isYourself,
            alternative_signer_id: null,
          };

          // If signature.title is empty and matchedSigner has a valid role, use it as fallback
          // Only use matchedSigner.role if it's not empty (assignment might not have a role)
          if (
            !preservedTitle &&
            matchedSigner.role &&
            matchedSigner.role.trim() !== ''
          ) {
            signature.title = matchedSigner.role;
          } else if (preservedTitle) {
            // Ensure signature.title is preserved (source of truth)
            signature.title = preservedTitle;
          }
          return;
        }
      }

      // PRIORITY 3: If no match by userId, try to match by name (legacy)
      if (signature.name) {
        const matchedSigner = this.newSelectableNames.find(
          signer => signer.full_name === signature.name,
        );
        if (matchedSigner) {
          // signature.title is the source of truth (saved to DB) - preserve it
          // selectedSigner.role is read-only from matchedSigner
          const preservedTitle = signature.title || '';

          // Create new selectedSigner with read-only role from matched signer
          signature.selectedSigner = {
            id: matchedSigner.id,
            full_name: matchedSigner.full_name,
            email: matchedSigner.email,
            role: matchedSigner.role, // Read-only from matched signer
            displayText: matchedSigner.displayText,
            isYourself: matchedSigner.isYourself,
            alternative_signer_id: null,
          };

          // If signature.title is empty and matchedSigner has a valid role, use it as fallback
          // Only use matchedSigner.role if it's not empty (assignment might not have a role)
          if (
            !preservedTitle &&
            matchedSigner.role &&
            matchedSigner.role.trim() !== ''
          ) {
            signature.title = matchedSigner.role;
          } else if (preservedTitle) {
            // Ensure signature.title is preserved (source of truth)
            signature.title = preservedTitle;
          }
          return;
        }
      }

      // If no match found, set to null
      signature.selectedSigner = null;
    },

    getDisplayEmail(email) {
      // Check if email matches the test pattern "test+...@soraban.com"
      if (this.isTestEmail(email)) {
        return 'No Email';
      }
      return email;
    },

    isTestEmail(email) {
      return email && email.match(/^test\+.*@soraban\.com$/);
    },

    processSignatures() {
      if (this.currentForm && this.currentForm.signatures) {
        this.currentForm.signatures.forEach(signature => {
          if (signature.selectedSigner) {
            // Initialize alternative_signer_id if it doesn't exist
            if (
              !signature.selectedSigner.hasOwnProperty('alternative_signer_id')
            ) {
              this.$set(
                signature.selectedSigner,
                'alternative_signer_id',
                null,
              );
            }

            // Update userId based on alternative signer settings
            if (signature.selectedSigner.alternative_signer_id) {
              this.$set(
                signature,
                'userId',
                signature.selectedSigner.alternative_signer_id,
              );
            }
          }
        });
      }
    },

    processAllSignatures() {
      if (this.taxReturn && this.taxReturn.e_sign_forms) {
        this.taxReturn.e_sign_forms.forEach(form => {
          if (form.signatures) {
            form.signatures.forEach(signature => {
              // First, match the existing signer to restore UI fields from newSelectableNames
              this.matchExistingSigner(signature);

              if (signature.selectedSigner) {
                // Initialize alternative_signer_id if it doesn't exist
                if (
                  !signature.selectedSigner.hasOwnProperty(
                    'alternative_signer_id',
                  )
                ) {
                  this.$set(
                    signature.selectedSigner,
                    'alternative_signer_id',
                    null,
                  );
                }

                // Update userId based on alternative signer settings
                if (signature.selectedSigner.alternative_signer_id) {
                  this.$set(
                    signature,
                    'userId',
                    signature.selectedSigner.alternative_signer_id,
                  );
                }
              }
            });
          }
        });
      }
    },

    getAvailableEmails() {
      // Get all signers except the current one, Yourself, currentUser, and filter out test emails
      return this.newSelectableNames
        .filter(
          signer =>
            signer.id !== this.currentUser.id &&
            !signer.isYourself &&
            !this.isTestEmail(signer.email),
        )
        .map(signer => {
          return {
            id: signer.id,
            email: signer.email,
            full_name: signer.full_name,
            displayText: `${signer.full_name} (${signer.email})`,
          };
        });
    },

    async saveSignature() {
      try {
        this.savingSignature = true;

        const previouslySavedTaxReturn = await this.save();
        this.$emit('on-save-signature', previouslySavedTaxReturn);

        const signature = this.currentForm.signatures[
          this.currentSignatureIndex
        ];

        // For some reason, some browsers do not return the correct image data
        // This is rare and we need to prevent storing invalid image data
        if (
          this.signatureType === 'image' &&
          !this.imageSignature &&
          !this.imageSignature.startsWith('data:image/png;base64,')
        ) {
          this.$store.dispatch('application/setErrorNotification', {
            message: "We couldn't capture your signature. Please try again.",
          });
          return;
        }

        this.savedSignaturesByName[signature.name] = {
          signatureType: this.signatureType,
          signatureData:
            this.signatureType === 'image'
              ? this.imageSignature
              : this.textSignature,
          signatureRawData:
            this.signatureType === 'image' ? this.imageRawData : null,
        };

        // Ensure date is set
        const currentDate = moment().format('L');
        signature.signatureDate = currentDate;
        signature.signatureData =
          this.signatureType === 'image'
            ? this.imageSignature
            : this.textSignature;
        signature.signatureType = this.signatureType;

        const saveData = {
          id: this.taxReturn.id,
          companyId: this.currentCompany.id,
          formIndex: this.currentFormIndex - 1,
          signatureIndex: this.currentSignatureIndex,
          signatureType: this.signatureType,
          signatureData:
            this.signatureType === 'image'
              ? this.imageSignature
              : this.textSignature,
          signatureRawData:
            this.signatureType === 'image' ? this.imageRawData : null,
          signatureDate: currentDate,
          sessionId: null,
          userId: signature.userId,
          title: signature.title,
          name: signature.name,
          email: signature.email,
          signatureLeft: signature.signatureLeft,
          signatureBottom: signature.signatureBottom,
          signatureWidth: signature.signatureWidth,
          signatureHeight: signature.signatureHeight,
          signatureDateLeft: signature.signatureDateLeft,
          signatureDateBottom: signature.signatureDateBottom,
          signatureDateWidth: signature.signatureDateWidth,
          signatureDateHeight: signature.signatureDateHeight,
        };

        saveData.selectedSigner = signature.selectedSigner;

        const response = await API.taxReturn.saveSignature(saveData);

        const taxReturn = response.data;

        this.$emit('on-save-signature', taxReturn);

        this.renderPdfWithAnnotations();
      } catch (error) {
        console.log(error);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem signing the document.',
          errorToSend: error,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.savingSignature = false;
        this.signaturePadOpened = false;
      }
    },

    async analyzeAllESignForms() {
      const formsToAnalyze = this.taxReturn.e_sign_forms
        .filter(
          form => form.form_id === 'unknown' && form.status !== 'analyzed',
        )
        .map(form => {
          form.status = 'analyzing';
          return form;
        });

      const sema = new Sema(4); // Limit the number of concurrent requests

      const analyzeForm = async form => {
        await sema.acquire();

        const pages = [form.page];

        try {
          const response = await axios.post(
            `/tax_returns/${this.taxReturn.id}/analyze_e_sign_forms`,
            {
              company_id: this.currentCompany.id,
              pages,
            },
          );

          response.data.forEach(result => {
            if (form.page === result.page) {
              form.form_id = result.form_id;
              form.form_name = result.form_name;
              form.signatures = result.signatures;
              form.status = 'analyzed';

              // Match the newly analyzed signatures to selectedSigner objects
              if (form.signatures && Array.isArray(form.signatures)) {
                form.signatures.forEach(signature => {
                  this.matchExistingSigner(signature);
                });
              }
            }
          });
        } catch (error) {
          form.status = 'analyze_failed';
        } finally {
          sema.release();
        }
      };

      const analyzeTasks = formsToAnalyze.map(form => analyzeForm(form));

      await Promise.all(analyzeTasks);
    },

    async updateCurrentFormIndex($event) {
      let newIndex = parseInt($event.target.value);

      // Make sure the index is not out of bounds
      if (newIndex < 1) {
        newIndex = 1;
      } else if (newIndex > this.formCount) {
        newIndex = this.formCount;
      }

      this.currentFormIndex = newIndex;
      $event.target.value = newIndex;
      await this.renderPdfWithAnnotations();
    },

    async getFormDefinitions() {
      const response = await API.signatureRequest.getFormDefinitions();

      this.formDefinitions = response.data.concat({
        id: 'other',
        name: 'Other (custom)',
        taxpayer_signature: null,
        spouse_signature: null,
      });
    },

    async getDefaultSignatureValues() {
      const response = await API.taxReturn.getDefaultSignatureValues();

      this.defaultSignatureValues = response.data;
    },

    getFormDefinitionById(id) {
      return this.formDefinitions.find(form => form.id === id);
    },

    addSigner() {
      const { width: pageWidth, height: pageHeight } = {
        width: this.pdfPageWidth,
        height: this.pdfPageHeight,
      };

      const defaultPageWidth = this.defaultSignatureValues.default_page_width;

      const ratio = pageWidth / defaultPageWidth;

      const signatureWidth =
        this.defaultSignatureValues.default_signature_width * ratio;
      const signatureHeight =
        this.defaultSignatureValues.default_signature_height * ratio;
      const signatureDateWidth =
        this.defaultSignatureValues.default_signature_date_width * ratio;
      const signatureDateHeight =
        this.defaultSignatureValues.default_signature_date_height * ratio;
      const signatureTextSize =
        this.defaultSignatureValues.default_signature_text_size * ratio;

      const indexOffset = this.currentForm.signatures.length * 80;

      // put in middle of page
      const signatureLeft = (pageWidth - signatureWidth) / 2 - 100;
      const signatureBottom = pageHeight / 2 - indexOffset;
      const signatureDateLeft = (pageWidth - signatureDateWidth) / 2 + 100;
      const signatureDateBottom = pageHeight / 2 - indexOffset;

      // Initialize with default values
      // Don't set a default title - let the user assign a role explicitly
      const newSignature = {
        title: '',
        name: '',
        userId: null,
        pageWidth,
        pageHeight,
        signatureWidth,
        signatureHeight,
        signatureBottom,
        signatureLeft,
        signatureDateWidth,
        signatureDateHeight,
        signatureDateLeft,
        signatureDateBottom,
        signatureTextSize,
      };

      newSignature.selectedSigner = null;

      this.currentForm.signatures.push(newSignature);

      this.makeDraggable();
      this.save(true);
    },

    removeSigner(index) {
      this.currentForm.signatures.splice(index, 1);
      this.save(true);
    },

    async removeForm() {
      const confirmed = window.confirm(
        'Are you sure you want to remove this form? This form will no longer classify as an e-sign form.',
      );

      if (!confirmed) {
        return;
      }

      const formIndexToDelete = this.currentFormIndex;
      const deletedForm = this.taxReturn.e_sign_forms[formIndexToDelete - 1];

      // Store the deleted form
      this.deletedESignForms.push(deletedForm);

      // We have to reset the index before removing the form
      if (this.currentFormIndex > 1) {
        this.currentFormIndex = this.currentFormIndex - 1;
      } else {
        this.currentFormIndex = 1;
      }

      this.taxReturn.e_sign_forms.splice(formIndexToDelete - 1, 1);

      await this.save();
    },

    getDeletedFormName(form) {
      if (form.form_id === 'other') {
        return !isEmpty(form.custom_form_name)
          ? form.custom_form_name
          : 'Other';
      } else if (this.getFormDefinitionById(form.form_id)) {
        return `${this.getFormDefinitionById(form.form_id).name} (${
          form.form_id
        })`;
      } else {
        return 'Unknown';
      }
    },

    async restoreDeletedForm(index) {
      const deletedForm = this.deletedESignForms[index];
      this.taxReturn.e_sign_forms.push(deletedForm);

      this.taxReturn.e_sign_forms = this.taxReturn.e_sign_forms.sort((a, b) => {
        if (typeof a.page === 'undefined' || a.page === null) return 1;
        if (typeof b.page === 'undefined' || b.page === null) return -1;
        return a.page - b.page;
      });

      this.deletedESignForms.splice(index, 1);
      this.currentFormIndex = this.taxReturn.e_sign_forms.length;
      await this.save();
    },

    getPageRatio(pageWidth) {
      if (this.pdfPageWidth === null) {
        return 1;
      }

      const ratio = this.pdfPageWidth / pageWidth;
      const maxRatio = 1.5;

      return Math.min(ratio, maxRatio);
    },

    getStylePosition({ pageWidth, elementBottom, elementLeft }) {
      const ratio = this.getPageRatio(pageWidth);

      return {
        bottom: elementBottom * ratio + 'px',
        left: elementLeft * ratio + 'px',
        transition: this.autoPlacingSignatures
          ? 'bottom 0.2s ease, left 0.2s ease'
          : 'none',
        pointerEvents: this.autoPlacingSignatures ? 'none' : 'auto',
        cursor: this.autoPlacingSignatures ? 'not-allowed' : 'move',
      };
    },

    getStyleDimension({ pageWidth, elementWidth, elementHeight }) {
      const ratio = this.getPageRatio(pageWidth);

      return {
        width: elementWidth * ratio + 'px',
        height: elementHeight * ratio + 'px',
      };
    },

    getStyleSignatureText({ pageWidth, fontSize }) {
      const ratio = this.getPageRatio(pageWidth);

      return {
        fontSize: fontSize * ratio + 'px',
      };
    },

    getStyleSignatureDateText({ pageWidth, fontSize }) {
      const ratio = this.getPageRatio(pageWidth);

      return {
        fontSize: fontSize * ratio + 'px',
      };
    },

    getStyleForSignatureHeader({ pageWidth, fontSize }) {
      const ratio = this.getPageRatio(pageWidth);

      return {
        fontSize: (fontSize / 1.5) * ratio + 'px',
      };
    },

    getStyleForSignatureTitle({ pageWidth, fontSize }) {
      const ratio = this.getPageRatio(pageWidth);

      return {
        fontSize: (fontSize / 2) * ratio + 'px',
      };
    },

    makeDraggable() {
      interact('.signature-container').draggable({
        listeners: {
          move: event => {
            const target = event.target;
            const x =
              (parseFloat(target.getAttribute('data-x')) || 0) + event.dx;
            const y =
              (parseFloat(target.getAttribute('data-y')) || 0) + event.dy;

            event.target.style.transform = `translate(${x}px, ${y}px)`;
            target.setAttribute('data-x', x);
            target.setAttribute('data-y', y);
          },

          end: event => {
            const index = parseInt(event.target.dataset['signatureid']);
            const signature = this.currentForm.signatures[index];

            const target = event.target;
            const pageRatio = this.getPageRatio(signature.pageWidth);

            const deltaLeft =
              (parseFloat(target.getAttribute('data-x')) || 0) / pageRatio;
            const deltaBottom =
              (parseFloat(target.getAttribute('data-y')) || 0) / pageRatio;

            const newLeft = signature.signatureLeft + deltaLeft;
            const newBottom = signature.signatureBottom - deltaBottom;

            signature.signatureLeft = newLeft;
            signature.signatureBottom = newBottom;

            target.setAttribute('data-x', 0);
            target.setAttribute('data-y', 0);
            target.style.transform = 'none';

            if (this.currentForm.form_id === 'other') {
              this.saveCustomFormSignatures(this.currentForm.signatures);
            }

            signature.shouldCollectSignaturePosition = true;

            this.checkAndFixPageDimension(this.currentForm);

            this.save(true);
          },
        },
      });

      interact('.signature-date-container').draggable({
        listeners: {
          move: event => {
            const target = event.target;
            const x =
              (parseFloat(target.getAttribute('data-x')) || 0) + event.dx;
            const y =
              (parseFloat(target.getAttribute('data-y')) || 0) + event.dy;

            event.target.style.transform = `translate(${x}px, ${y}px)`;
            target.setAttribute('data-x', x);
            target.setAttribute('data-y', y);
          },

          end: event => {
            const index = parseInt(event.target.dataset['signatureid']);
            const signature = this.currentForm.signatures[index];

            const target = event.target;
            const pageRatio = this.getPageRatio(signature.pageWidth);

            const deltaLeft =
              (parseFloat(target.getAttribute('data-x')) || 0) / pageRatio;
            const deltaBottom =
              (parseFloat(target.getAttribute('data-y')) || 0) / pageRatio;

            const newLeft = signature.signatureDateLeft + deltaLeft;
            const newBottom = signature.signatureDateBottom - deltaBottom;

            signature.signatureDateLeft = newLeft;
            signature.signatureDateBottom = newBottom;

            target.setAttribute('data-x', 0);
            target.setAttribute('data-y', 0);
            target.style.transform = 'none';

            if (this.currentForm.form_id === 'other') {
              this.saveCustomFormSignatures(this.currentForm.signatures);
            }

            if (!signature.hideSignatureDate) {
              signature.shouldCollectDatePosition = true;
            }

            this.checkAndFixPageDimension(this.currentForm);

            this.save(true);
          },
        },
      });
    },

    async createPdfDocument(documentPath) {
      const response = await API.integration.downloadDocument({
        companyId: this.currentCompany.id,
        link: documentPath,
      });

      const pdfSrc = response.data.document.data.link;
      const pdfBytes = await fetch(pdfSrc).then(res => res.arrayBuffer());
      return PDFDocument.load(pdfBytes);
    },

    async downloadDocumentAsBuffer(documentLink) {
      const response = await API.integration.downloadDocument({
        companyId: this.currentCompany.id,
        link: this.currentCompany.upload_link.concat('/', documentLink),
      });

      const url = response.data.document.data.link;

      return fetch(url).then(res => res.arrayBuffer());
    },

    async download(downloadType) {
      const pdfDoc = await this.createPdfDocument(
        this.taxReturn.watermark_enabled
          ? this.taxReturn.watermarked_file
          : this.taxReturn.original_file,
      );

      const zip = new JSZip();

      // Load font for date
      const dateFontUrl = window.location.origin + '/static/fonts/Mali.ttf';
      const dateFontBytes = await fetch(dateFontUrl).then(res =>
        res.arrayBuffer(),
      );

      // Load font for text signature
      const signatureFontUrl =
        window.location.origin + '/static/fonts/Zeyada.ttf';
      const signatureFontBytes = await fetch(signatureFontUrl).then(res =>
        res.arrayBuffer(),
      );

      let esignForms = [];

      esignForms = this.taxReturn.e_sign_forms;

      let index = 1;
      for (const esignForm of esignForms) {
        const hasSignatures =
          esignForm.signatures.length > 0 &&
          esignForm.signatures.some(signature => signature.signatureData);

        // If download type is signed only but the form has no signatures, the no-op
        if (downloadType === 'signed-only' && !hasSignatures) {
          continue;
        }

        const esignPdf = await PDFDocument.create();
        esignPdf.registerFontkit(fontkit);

        const dateFont = await esignPdf.embedFont(dateFontBytes);
        const signatureFont = await esignPdf.embedFont(signatureFontBytes);

        const [esignPage] = await esignPdf.copyPages(pdfDoc, [
          esignForm.page - 1,
        ]);

        // Handle page rotation for signature placement.
        //
        // pdf-lib draws in the raw (unrotated) coordinate space, but
        // signature positions were captured in the visual (rotated)
        // coordinate space via pdf.js getViewport(). For rotated pages
        // we transform each element's anchor point from visual to raw
        // space and counter-rotate the drawn content so it appears
        // upright after the viewer applies the page rotation.
        //
        // IMPORTANT: drawImage/drawText `width` and `height` must stay
        // in *visual* dimensions. The `rotate` parameter handles the
        // orientation. Only the anchor (x, y) needs to be mapped to raw
        // space. Mapping both corners and deriving width/height from the
        // raw bounding box swaps the aspect ratio on 90°/270° pages,
        // which makes the signature appear squashed.
        const rotation = esignPage.getRotation().angle;
        const { width: rawWidth, height: rawHeight } = esignPage.getSize();
        const isRotated = rotation === 90 || rotation === 270;
        const pageWidth = isRotated ? rawHeight : rawWidth;
        const pageHeight = isRotated ? rawWidth : rawHeight;

        for (const signature of esignForm.signatures) {
          if (signature.signatureData) {
            const ratio = pageWidth / signature.pageWidth;
            const ratioY = pageHeight / signature.pageHeight;

            if (signature.signatureType === 'image') {
              const image = await esignPdf.embedPng(signature.signatureData);
              const vx = signature.signatureLeft * ratio;
              const vy = signature.signatureBottom * ratioY;
              const vw = signature.signatureWidth * ratio;
              const vh = signature.signatureHeight * ratioY;

              if (rotation === 0) {
                esignPage.drawImage(image, {
                  x: vx,
                  y: vy,
                  width: vw,
                  height: vh,
                });
              } else {
                // Transform only the anchor point to raw space.
                // Keep visual width/height — the rotate parameter
                // handles orientation. Using bounding-box math here
                // would swap w/h on 90°/270° and squash the image.
                const anchor = this.visualToRaw(
                  vx,
                  vy,
                  rotation,
                  rawWidth,
                  rawHeight,
                );
                esignPage.drawImage(image, {
                  x: anchor.rx,
                  y: anchor.ry,
                  width: vw,
                  height: vh,
                  rotate: degrees(rotation),
                });
              }
            } else {
              this.drawTextWithScaling({
                page: esignPage,
                text: signature.signatureData,
                font: signatureFont,
                fontSize: signature.signatureTextSize * ratio,
                boxLeft: signature.signatureLeft * ratio,
                boxWidth: signature.signatureWidth * ratio,
                boxBottom: signature.signatureBottom * ratioY,
                boxHeight: signature.signatureHeight * ratioY,
                verticalOffset: 0.7,
                rotation,
                rawWidth,
                rawHeight,
              });
            }

            if (!signature.hideSignatureDate && signature.signatureDate) {
              this.drawTextWithScaling({
                page: esignPage,
                text: signature.signatureDate,
                font: dateFont,
                fontSize: signature.signatureTextSize * ratio,
                boxLeft: signature.signatureDateLeft * ratio,
                boxWidth: signature.signatureDateWidth * ratio,
                boxBottom: signature.signatureDateBottom * ratioY,
                boxHeight: signature.signatureDateHeight * ratioY,
                verticalOffset: 0,
                rotation,
                rawWidth,
                rawHeight,
              });
            }
          }
        }

        esignPdf.addPage(esignPage);
        const esignData = await esignPdf.save();

        const formDefinition = this.getFormDefinitionById(esignForm.form_id);
        const fileName = `${index}-${
          esignForm.form_id === 'other' && !isEmpty(esignForm.custom_form_name)
            ? esignForm.custom_form_name
            : formDefinition
            ? formDefinition.name
            : esignForm.form_id
        }.pdf`;

        index++;

        zip.file(fileName, esignData, { binary: true });

        if (esignForm.wetSignatures) {
          for (const wetSignature of esignForm.wetSignatures) {
            const signedDocument = await this.downloadDocumentAsBuffer(
              wetSignature.signedDocumentPath,
            );

            // Prefix the file name with an index to prevent files from being overwritten
            const fileName =
              index +
              '-' +
              this.getDocumentName(wetSignature.signedDocumentPath);

            index++;

            zip.file(fileName, signedDocument, { binary: true });
          }
        }
      }

      const zipName =
        this.taxReturn.tax_return_name.replace('.pdf', '') +
        ' (E-sign forms).zip';

      zip.generateAsync({ type: 'blob' }).then(content => {
        download(content, zipName, 'application/zip');
      });
    },

    drawTextWithScaling({
      page,
      text,
      font,
      fontSize,
      boxLeft,
      boxWidth,
      boxBottom,
      boxHeight,
      verticalOffset,
      rotation = 0,
      rawWidth = 0,
      rawHeight = 0,
    }) {
      // Calculate exact text dimensions
      const textWidth = font.widthOfTextAtSize(text, fontSize);
      const textHeight = font.heightAtSize(fontSize);

      // Ensure text fits within the box
      const maxTextWidth = boxWidth * 0.9; // Leave 10% margin
      const maxTextHeight = boxHeight * 0.8; // Leave 20% margin

      // Adjust font size if text is too wide
      let adjustedFontSize = fontSize;
      if (textWidth > maxTextWidth) {
        adjustedFontSize = fontSize * (maxTextWidth / textWidth);
      }

      // Recalculate dimensions with adjusted font size
      const adjustedTextWidth = font.widthOfTextAtSize(text, adjustedFontSize);
      const adjustedTextHeight = font.heightAtSize(adjustedFontSize);

      // Position text with exact centering in visual space
      const textX = boxLeft + (boxWidth - adjustedTextWidth) / 2;
      // Calculate vertical offset based on provided parameter
      const verticalOffsetPx = Math.min(
        adjustedTextHeight * verticalOffset,
        maxTextHeight * (verticalOffset / 2),
      );
      const textY =
        boxBottom + (boxHeight - adjustedTextHeight) / 2 + verticalOffsetPx;

      if (rotation === 0) {
        page.drawText(text, {
          x: textX,
          y: textY,
          size: adjustedFontSize,
          font,
        });
      } else {
        // Transform visual text position to raw coordinates and
        // counter-rotate so the text appears upright after the
        // viewer applies the page rotation.
        const raw = this.visualToRaw(
          textX,
          textY,
          rotation,
          rawWidth,
          rawHeight,
        );
        page.drawText(text, {
          x: raw.rx,
          y: raw.ry,
          size: adjustedFontSize,
          font,
          rotate: degrees(rotation),
        });
      }
    },

    /**
     * Map a point from visual (rotated) coordinates to raw pdf-lib
     * coordinates. Visual coords come from pdf.js getViewport() which
     * accounts for the page /Rotate entry. Raw coords are what pdf-lib
     * uses for drawImage/drawText.
     *
     * @param {number} vx - Visual x coordinate
     * @param {number} vy - Visual y coordinate
     * @param {number} rotation - Page rotation in degrees (0, 90, 180, 270)
     * @param {number} rW - Raw (unrotated) page width
     * @param {number} rH - Raw (unrotated) page height
     * @returns {{ rx: number, ry: number }}
     */
    visualToRaw(vx, vy, rotation, rW, rH) {
      switch (rotation) {
        case 90:
          return { rx: rW - vy, ry: vx };
        case 180:
          return { rx: rW - vx, ry: rH - vy };
        case 270:
          return { rx: vy, ry: rH - vx };
        default:
          return { rx: vx, ry: vy };
      }
    },

    // Please don't forget to update ESignStep too if you change this method
    async updatePdfPageDimensions() {
      if (!this.pdfDoc) return;

      const page = await this.pdfDoc.getPage(this.pdfPageIndex);
      const scale = this.getScale(page);
      const viewport = page.getViewport({ scale });

      this.pdfPageWidth = viewport.width;
      this.pdfPageHeight = viewport.height;
    },

    next() {
      this.$emit('next');
    },

    async markCurrentFormAsReviewed() {
      this.currentForm.reviewed_by = this.currentUser.id;
      await this.save();
    },

    getUnreviewedForms() {
      return this.taxReturn.e_sign_forms.filter(form => {
        return !form.reviewed_by;
      });
    },

    hasUnreviewedForms() {
      return this.getUnreviewedForms().length > 0;
    },

    hasErrors() {
      return this.taxReturn.e_sign_forms.some(form => {
        return this.isFormInvalid(form);
      });
    },

    isFormInvalid(form) {
      if (form.form_id === 'unknown') {
        return true;
      } else {
        return form.signatures.some(signature => {
          if (signature.userId === null) {
            return true;
          } else if (isEmpty(signature.name)) {
            return true;
          } else {
            return false;
          }
        });
      }
    },

    async showSavingDialog() {
      if (this.savingDialogOpened) {
        return;
      }

      try {
        this.savingDialogOpened = true;
        const taxReturn = await this.save(false);

        if (taxReturn) {
          this.$emit('e-sign-forms-updated', taxReturn);
        }
      } finally {
        this.savingDialogOpened = false;
      }
    },

    async beforeClose() {
      await this.showSavingDialog();
      return true;
    },

    async save(forceSave = false) {
      if (!this.editable && !forceSave) {
        return;
      }

      try {
        this.saving = true;

        // Create a deep copy of e_sign_forms for saving
        const e_sign_forms_copy = JSON.parse(
          JSON.stringify(this.taxReturn.e_sign_forms),
        );

        // Strip UI-only fields from the copy for database storage
        e_sign_forms_copy.forEach(form => {
          if (form.signatures) {
            form.signatures.forEach(signature => {
              if (signature.selectedSigner) {
                signature.selectedSigner = {
                  id: signature.selectedSigner.id,
                  alternative_signer_id:
                    signature.selectedSigner.alternative_signer_id || null,
                };
              }
            });
          }
        });

        const response = await axios.patch(
          `/tax_returns/${this.taxReturn.id}`,
          {
            company_id: this.taxReturn.company_id,
            e_sign_forms: e_sign_forms_copy,
          },
        );
        return response.data;
      } finally {
        this.saving = false;
      }
    },

    async saveCustomFormSignatures(signatures) {
      const firmCustomFormName = this.customFormNames.find(
        customFormName =>
          customFormName.name === this.currentForm.custom_form_name,
      );

      const signaturesToSave = uniqBy(
        [
          ...signatures,
          ...(firmCustomFormName.signatures || []), // Make sure we also keep the existing signatures (Spouse's signature box)
        ],
        'title',
      );

      const response = await API.firm.updateCustomFormName({
        firmId: this.currentUserFirm.id,
        id: firmCustomFormName.id,
        signatures: signaturesToSave.map(signature => {
          return pick(signature, [
            'title',
            'pageWidth',
            'pageHeight',
            'signatureWidth',
            'signatureHeight',
            'signatureBottom',
            'signatureLeft',
            'signatureDateWidth',
            'signatureDateHeight',
            'signatureDateLeft',
            'signatureDateBottom',
            'signatureTextSize',
            'hideSignatureDate',
          ]);
        }),
      });

      await this.getCustomFormNames();
    },

    async finishReviewing(skipUnreviewed = false) {
      if (this.hasIncompleteNoEmailSigners) {
        this.noEmailWarningDialogOpened = true;
        return;
      }

      if (this.hasSignersWithMissingRole) {
        this.missingRoleWarningDialogOpened = true;
        return;
      }

      if (this.hasErrors()) {
        this.erroredFormsWarningDialogOpened = true;
        this.shouldHighlightErroredForms = true;
        this.validateInputs();
        return;
      }

      if (!skipUnreviewed && this.hasUnreviewedForms()) {
        this.unreviewedFormsWarningDialogOpened = true;
        this.shouldHighlightUnreviewedForms = true;
        return;
      }

      const taxReturn = await this.save();
      this.$emit('e-sign-forms-updated', taxReturn);
    },

    validateInputs() {
      this.$refs.form.validate();
    },

    async getCustomFormNames() {
      const response = await API.firm.getCustomFormNames({
        firmId: this.currentUserFirm.id,
      });

      this.customFormNames = response.data;
    },

    async handleCustomFormChange(item) {
      // Depends on whether item is selected or typed, it can be either an object or just a string;
      const name = item && item.name ? item.name : item;
      this.currentForm.custom_form_name = name;

      if (isEmpty(name)) {
        return;
      }

      const customFormName = this.customFormNames.find(
        customFormName => customFormName.name === name,
      );

      if (!customFormName) {
        const response = await API.firm.addCustomFormName({
          firmId: this.currentUserFirm.id,
          name,
        });

        this.customFormNames.push(response.data);
      } else if (!this.taxReturnUseNewSignatureAlgorithmEnabled) {
        // Initialize saved signatures
        const response = await API.firm.getInitialSignatures({
          firmId: this.currentUserFirm.id,
          id: customFormName.id,
          companyId: this.currentCompany.id,
        });

        this.currentForm.signatures = response.data;
        // Process signatures to set has_no_email property
        this.processSignatures();
      }
    },

    async deleteCustomFormName(item) {
      const response = await API.firm.deleteCustomFormName({
        firmId: this.currentUserFirm.id,
        id: item.id,
      });

      this.customFormNames = this.customFormNames.filter(
        customFormName => customFormName.id !== item.id,
      );
    },

    getScale(page) {
      // This scale makes PDF look bigger
      let initialScale = 1.5;

      const initialViewport = page.getViewport({ scale: initialScale });

      const maxWidth = this.MAX_PDF_WIDTH;

      let newScale = initialScale;

      // If the initialScale makes the PDF too big, we reduce it so that it fits within max width
      if (initialViewport.width > maxWidth) {
        newScale = (maxWidth / initialViewport.width) * initialScale;
      }

      return newScale;
    },

    async renderPdfWithAnnotations() {
      if (!this.pdfDoc) return;

      const page = await this.pdfDoc.getPage(this.pdfPageIndex);
      const scale = this.getScale(page);
      const viewport = page.getViewport({ scale });

      // Fallback to PDF rendering if pre-processed images are not available
      if (!this.pageImages || !this.pageImages[this.currentForm.page - 1]) {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({
          canvasContext: context,
          viewport,
        }).promise;

        const container = document.getElementById('pdf-annotation');
        container.innerHTML = '';
        container.appendChild(canvas);
      }

      // Store the actual rendered width (at 1.5 scale)
      this.pdfPageWidth = viewport.width;
      this.pdfPageHeight = viewport.height;

      this.pdfLoaded = true;
    },

    async loadPageImages() {
      try {
        if (this.taxReturn.original_images) {
          const response = await API.taxReturn.getOriginalImages({
            id: this.taxReturn.id,
            companyId: this.currentCompany.id,
          });

          this.pageImages = response.data.map(image => {
            return image.url;
          });
        }
      } catch (error) {
        console.error({ error });
      }
    },

    async reRenderPageImages() {
      try {
        const numPages = this.pdfDoc.numPages;
        for (let pageNum = 1; pageNum <= numPages; pageNum++) {
          const page = await this.pdfDoc.getPage(pageNum);
          const scale = this.getScale(page);
          const viewport = page.getViewport({ scale });
          const canvas = document.createElement('canvas');
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          await page.render({
            canvasContext: canvas.getContext('2d'),
            viewport,
          }).promise;
          this.$set(this.pageImages, pageNum - 1, canvas.toDataURL());
        }
      } catch (error) {
        console.error('Error re-rendering page images:', error);
      }
    },

    // In some cases, the `pageHeight` we specified in the backend is incorrect
    // We need to check and fix them here
    checkAndFixPageDimension(form) {
      form.signatures.forEach(signature => {
        const expectedRatio = this.pdfPageWidth / this.pdfPageHeight;
        const actualRatio = signature.pageWidth / signature.pageHeight;

        if (Math.abs(expectedRatio - actualRatio) > 0.01) {
          // Ratios differ significantly, fix the pageHeight
          const correctedPageHeight =
            (signature.pageWidth * this.pdfPageHeight) / this.pdfPageWidth;

          signature.pageHeight = correctedPageHeight;
        }
      });
    },

    handleKeyDown(event) {
      // Check for Ctrl + D (or Cmd + D on Mac)
      if ((event.ctrlKey || event.metaKey) && event.key === 'd') {
        event.preventDefault();
        this.shouldShowDebugingTools = true;

        this.$store.dispatch('application/setSuccessNotification', {
          message: 'Debugging tools enabled',
        });
      }
    },
  },

  watch: {
    async currentFormIndex() {
      if (this.currentForm) {
        this.pdfPageIndex = this.currentForm.page;

        // Match existing signers when form changes (only for new flow)
        if (this.currentForm.signatures) {
          this.currentForm.signatures.forEach(signature => {
            this.matchExistingSigner(signature);
          });
        }

        // Process signatures to ensure has_no_email property is set
        this.processSignatures();

        await this.markCurrentFormAsReviewed();

        if (this.shouldHighlightErroredForms) {
          this.validateInputs();
        }

        await this.renderPdfWithAnnotations();
      }
    },

    async pdfLoaded(value) {
      if (value) {
        await this.markCurrentFormAsReviewed();
        this.updatePdfPageDimensions();
      }
    },

    taxReturn() {
      // Process all signatures when taxReturn data is loaded
      this.processAllSignatures();
    },
  },

  async created() {
    await this.getDefaultSignatureValues();
    await this.getFormDefinitions();
  },

  async beforeDestroy() {
    window.removeEventListener('resize', this.updatePdfPageDimensions);
    // Remove keyboard event listener
    window.removeEventListener('keydown', this.handleKeyDown);

    await this.beforeClose();
  },

  async mounted() {
    // Add keyboard event listener
    window.addEventListener('keydown', this.handleKeyDown);

    this.getCustomFormNames();

    try {
      this.pdfLoading = true;

      // Load pre-processed images if available
      await this.loadPageImages();

      // Step 1: Fetch the document from API
      const response = await API.integration.downloadDocument({
        companyId: this.currentCompany.id,
        link: this.taxReturn.original_file,
      });

      if (!response.data.document) {
        console.error('No document data found.');
        return;
      }

      const pdfSrc = response.data.document.data.link;

      // Step 2: Load PDF using pdf.js
      const pdflib = window.pdfjsLib;
      pdflib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_SRC;

      const loadingTask = pdflib.getDocument({
        url: pdfSrc,
        cMapUrl: PDFJS_CMAP_URL,
        cMapPacked: true,
        standardFontDataUrl: PDFJS_STANDARD_FONT_DATA_URL,
      });
      this.pdfDoc = await loadingTask.promise;

      // Re-render pre-processed images in background with correct font config
      // to fix broken font rendering on certain tax returns.
      if (this.pageImages) {
        this.reRenderPageImages();
      }

      // Step 3: Extract pages and render them properly

      // Step 4: Set the page index
      this.pdfPageIndex = this.taxReturn.e_sign_forms[
        this.currentFormIndex - 1
      ].page;

      // Match existing signers when component loads (only for new flow)
      if (this.currentForm && this.currentForm.signatures) {
        this.currentForm.signatures.forEach(signature => {
          this.matchExistingSigner(signature);
        });
      }

      await this.renderPdfWithAnnotations();
      this.updatePdfPageDimensions();

      // Handle resizing events
      window.addEventListener('resize', this.updatePdfPageDimensions);

      this.makeDraggable();

      // Step 5: Ensure interactive fields (annotations, signatures) are rendered
      if (this.taxReturn.status === 'action_required') {
        this.analyzeAllESignForms();
      }
    } catch (error) {
      console.error('Error loading PDF:', error);
    } finally {
      this.pdfLoading = false;
    }
  },
};
</script>

<style lang="postcss" scoped>
.fullpage {
  height: calc(100dvh - 60px); /* 60px is the height of toolbar */
}

.sc-alert {
  position: sticky;
  top: 0;

  margin: 0px;
  padding: 8px;

  background-color: var(--blue-600);
  color: var(--gray-0);
}

.tab-content-container {
  flex-direction: column;
  background-color: #ebebeb;
}

.instruction {
  margin: 0px;
  padding: 3px 16px;
  border: none;
}

.sidebar {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 20px;
  width: 100%;
  max-width: 260px;
  overflow: auto;
  padding: 20px 16px 32px;

  background-color: #fff;
  border-right: 1px solid var(--gray-300);
  box-shadow: 1px 1px 3px 0px rgba(0, 0, 0, 0.15);
}

@media (max-width: 860px) {
  .sidebar {
    display: none;
  }
}

.pdf-container {
  flex: auto;
  position: relative;
}

.toolbar {
  box-sizing: border-box;
  align-items: center;
  display: flex;
  gap: 16px;
  height: 56px;
  justify-content: space-between;
  padding: 0 8px;
  width: 100%;
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: var(--gray-100);
  border-bottom: 1px solid var(--gray-300);
}

.toolbar__pageNavigation {
  display: flex;
  justify-content: center;
  min-width: calc(100% - 360px);
}

.toolbar__pageActions {
  width: 360px;
}

.toolbar__downloadMenu {
  z-index: 1000;
}

.toolbar input {
  height: 30px;
  padding: 0;
  width: 40px;

  border: 0;
  border-radius: 2px;
  outline: 1px solid var(--gray-300);
  background-color: var(--gray-0);

  font-family: var(--font-family-sans);
  line-height: 32px;
  text-align: center;
}

.toolbar input:active,
.toolbar input:focus {
  outline: 1px solid var(--blue-400);
}

.page-container {
  flex: auto;
  width: 100%;
  height: 100%;
}

.scrollable-container {
  overflow: auto;
  width: 0;

  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.15);
}

.pdf-page-container {
  position: relative;

  flex-direction: column;
  height: max-content;
  width: 100%;
}

.pdf {
  position: relative;

  padding: 0;
  width: 100%;
  display: flex;
}

.page-image {
  width: 100%;
}

.page-list {
  padding-left: 0px;
}

.page-list li {
  list-style: none;
  padding: 8px 4px;
  border-bottom: 1px solid #eee;
}

.page-list li a {
  color: #444;
  text-decoration: none;
}

.page-list li a i {
  color: #444;
}

.page-list li.selected {
  background-color: rgba(0, 0, 0, 0.07);
  border-radius: 4px;
}

.page-list li.errored a,
.page-list li.errored a i,
.page-list li.unreviewed a,
.page-list li.unreviewed a i {
  color: var(--red-700);
}

.page-input >>> .v-input__control > .v-input__slot {
  border-radius: 4px;
  box-shadow: none !important;
  border: 1px solid #bbb;
}

.page-input >>> .v-input__control {
  min-height: 20px;
}

.page-input >>> input {
  width: 32px;
}

.page-input >>> input::-webkit-outer-spin-button,
.page-input >>> input::-webkit-inner-spin-button {
  -webkit-appearance: none;
}

.sidebar--right {
  box-sizing: border-box;
  overflow-y: auto;

  max-width: 320px;
  width: 100%;
  padding: 16px;

  background-color: #fff;
  border-left: 1px solid var(--gray-200);
}

.debug-container {
  margin-bottom: 14px;
  background-color: #f1f1f1;
  padding: 16px;
  border-radius: 12px;
}

.signer-container {
  margin-top: 12px;
  background-color: #f1f1f1;
  padding: 16px;
  border-radius: 12px;
}

.label {
  font-size: 15px;
  min-width: 60px;
}

.form-control {
  margin-bottom: 10px;

  & >>> .v-input {
  }

  & >>> .v-text-field .v-input__slot {
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.1) !important;
    border: 1px solid #ccc;
    border-radius: 8px;
  }

  & >>> .v-text-field .v-input__control {
    min-height: 20px;
  }

  & >>> .v-select__selections {
    overflow: hidden;
  }

  & >>> .v-select__selection--comma {
    flex: auto;
    width: 0;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.form-control .error--text {
  & >>> .v-input__slot {
    border: 1px solid var(--red-500) !important;
  }
}

.signature-container,
.signature-date-container {
  position: absolute;

  & >>> .sign-area {
    display: flex;
    flex-direction: column;
    height: 100%;

    background-color: rgba(76, 175, 80, 0.85);
    border: 2px dashed rgba(0, 0, 0, 0.75);
    border-radius: 4px;
    color: #fff;
    backdrop-filter: blur(0.5px);

    font-size: 1em;
    font-weight: bold;
    text-align: left;
    text-shadow: 0 1px 0 rgba(0, 0, 0, 0.25);
    user-select: none;
  }

  & >>> .sign-area .signer-title {
    position: absolute;
    top: -32px;
    left: -2px;

    padding: 2px 16px;

    background-color: rgba(40, 50, 80, 0.9);
    border-radius: 12px;
    border: 1px solid rgba(0, 0, 0, 0.5);
    color: rgba(255, 255, 255, 0.75);

    font-size: 14px !important;
    font-weight: medium;
    line-height: 24px !important;
  }

  & >>> .sign-area h2 {
    display: flex;
    flex: auto;
    align-items: center;
    padding: 0px 16px;
  }

  & >>> .sign-area .signer-title {
    padding: 0px 16px;
    flex: auto;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }

  & >>> .signature-text {
    font-family: 'Zeyada', cursive;
    /* Make sure the signature text appear in one line */
    white-space: nowrap;
  }

  & >>> .signature-date {
    font-family: 'Mali', cursive;
    /* Make sure the signature date appear in one line */
    white-space: nowrap;
  }
}

.email-display {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
}

.outer-container {
  min-height: 0;
}

.inner-container {
  min-height: 0;

  .sidebar {
    min-height: 0;
  }

  .pdf-container {
    display: flex;
    flex-direction: column;
    min-height: 0;

    .page-container {
      min-height: 0;
    }
  }
}
</style>
